import { forwardRef, useEffect, useImperativeHandle } from 'react'
import { FieldArray, FormikProvider, useField, useFormik } from 'formik'
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons'

import { makeNumberArraySchema, makeReqStringSchema } from '@/base'
import { selectAdminOrUserGroupedFinancialAccountOptions } from '@/selectors/financeSelectors'
import { getCurrentUser } from '@/selectors/user.selectors'
import { useReselector } from '@/utils/sharedHooks'
import { TypographyStyles } from '@/styles'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { Text, Button, Form, Icon } from '@/ui'
import {
  GroupedDropdown,
  type GroupedDropdownProps,
} from '@/components/GroupedDropdown'

export interface BankStatementUploadListHandle {
  validate: () => Promise<boolean>
}

interface BankStatementUploadListProps {
  files?: File[]
  onRemove?: (index: number) => void
  setExtraPayload: (
    payload:
      | Record<string, string | number | (string | number)[]>
      | ((
          prev: Record<string, string | number | (string | number)[]>
        ) => Record<string, string | number | (string | number)[]>)
  ) => void
}

interface FileFormEntry {
  fileName: string
  fileLastModified: number
  month: string
  accountIds: number[]
}

const monthSchema = makeReqStringSchema({ field: 'statement month' })

const accountIdsSchema = makeNumberArraySchema({ field: 'account' })

const filesAreEquivalent = (a: FileFormEntry[], b: FileFormEntry[]) => {
  if (a.length !== b.length) return false
  return a.every(
    (entry, i) =>
      entry.fileName === b[i].fileName &&
      entry.fileLastModified === b[i].fileLastModified
  )
}

const AccountsDropdownField = ({
  name,
  options,
}: {
  name: string
  options: GroupedDropdownProps<number | string>['options']
}) => {
  const [field, , helpers] = useField<number[]>(name)
  return (
    <GroupedDropdown
      options={options}
      value={field.value}
      onChange={(value) =>
        helpers.setValue(
          value.filter((id): id is number => typeof id === 'number')
        )
      }
      placeholder="Select accounts"
    />
  )
}

const FileRow = ({
  entry,
  index,
  onRemove,
  options,
}: {
  entry: FileFormEntry
  index: number
  onRemove: () => void
  options: GroupedDropdownProps<number | string>['options']
}) => (
  <div className="flex flex-col gap-2 py-3 border-b border-neutral-200 w-full">
    <div className="flex items-center justify-between gap-2">
      <Text className="overflow-hidden text-ellipsis whitespace-nowrap text-sm min-w-0">
        {entry.fileName}
      </Text>

      <div className="flex-shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={onRemove}
          className="h-8 w-8 p-0"
        >
          <Icon icon={faTrashCan} />
        </Button>
      </div>
    </div>

    <div className="flex items-start gap-2 w-full">
      <div className="w-[140px] shrink-0">
        <Form.DateInput
          name={`files[${index}].month`}
          placeholder="Month"
          className="w-full text-sm"
          dateFormat={DATE_FORMATS.MONTH_YEAR}
          showMonthYearPicker
          schema={monthSchema}
        />
      </div>

      <div className="flex-1 min-w-0">
        <Form.Field
          name={`files[${index}].accountIds`}
          schema={accountIdsSchema}
        >
          <div>
            <AccountsDropdownField
              name={`files[${index}].accountIds`}
              options={options}
            />
          </div>
        </Form.Field>
      </div>
    </div>
  </div>
)

export const BankStatementUploadList = forwardRef<
  BankStatementUploadListHandle,
  BankStatementUploadListProps
>(({ files = [], onRemove = (_index: number) => {}, setExtraPayload }, ref) => {
  const user = useReselector(getCurrentUser)
  const financialAccountOptions = useReselector(
    selectAdminOrUserGroupedFinancialAccountOptions,
    user?.id
  )

  const formik = useFormik({
    initialValues: { files: [] as FileFormEntry[] },
    onSubmit: () => {},
  })

  const { values, setFieldValue, setFieldTouched, validateForm } = formik

  useEffect(() => {
    const next = files.map((file) => {
      const existing = values.files.find(
        (entry) =>
          entry.fileName === file.name &&
          entry.fileLastModified === file.lastModified
      )
      return (
        existing ?? {
          fileName: file.name,
          fileLastModified: file.lastModified,
          month: '',
          accountIds: [],
        }
      )
    })
    if (!filesAreEquivalent(next, values.files)) {
      setFieldValue('files', next, false)
    }
  }, [files, values.files, setFieldValue])

  useEffect(() => {
    setExtraPayload((prev) => {
      const fileKeys = new Set<string>()
      values.files.forEach(({ fileName }) => {
        fileKeys.add(`${fileName}_month`)
        fileKeys.add(`${fileName}_accountId`)
      })
      const next: Record<string, string | number | (string | number)[]> = {}
      Object.entries(prev).forEach(([key, value]) => {
        if (!fileKeys.has(key)) {
          next[key] = value
        }
      })
      values.files.forEach(({ fileName, month, accountIds }) => {
        if (month) next[`${fileName}_month`] = month
        if (accountIds.length > 0) next[`${fileName}_accountId`] = accountIds
      })
      return next
    })
  }, [values.files, setExtraPayload])

  useImperativeHandle(
    ref,
    () => ({
      validate: async () => {
        await Promise.all(
          values.files.flatMap((_, idx) => [
            setFieldTouched(`files[${idx}].month`, true, false),
            setFieldTouched(`files[${idx}].accountIds`, true, false),
          ])
        )
        const errors = await validateForm()
        return values.files.length > 0 && Object.keys(errors).length === 0
      },
    }),
    [values.files, validateForm, setFieldTouched]
  )

  if (files.length === 0) return null

  return (
    <FormikProvider value={formik}>
      <div className="mt-4 overflow-x-hidden overflow-y-auto max-h-96 w-full">
        <Text variant={TypographyStyles.EYEBROW}>Documents to upload</Text>
        <Text variant={TypographyStyles.BODY_SM} className="mt-1">
          Select a statement month and at least one account for each file.
        </Text>

        <FieldArray
          name="files"
          render={() => (
            <div className="mt-2 w-full">
              {values.files.map((entry, index) => (
                <FileRow
                  key={`${entry.fileName}-${entry.fileLastModified}`}
                  entry={entry}
                  index={index}
                  onRemove={() => onRemove(index)}
                  options={financialAccountOptions}
                />
              ))}
            </div>
          )}
        />
      </div>
    </FormikProvider>
  )
})
